/**
 * Home-page CTA card for installing the PWA + enabling push
 * notifications.  Unlike the floating banner (which disappears after
 * one dismissal and depends on Chrome's `beforeinstallprompt` timing),
 * this card lives inline in the dashboard and stays visible until BOTH:
 *   1. Notifications are actually granted, AND
 *   2. The app is either installed OR the user is already running it
 *      in "standalone" display mode.
 *
 * iOS gets a dedicated instruction modal because Safari has no
 * programmatic install prompt — the user must use Share → Add to Home
 * Screen manually.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Bell, DownloadSimple, CheckCircle, ShareNetwork, Plus, LockKey, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { ensurePushEnabled, permissionState } from '@/utils/pwa';
import { toast } from 'sonner';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/i.test(ua);
}

export default function PWAHomeCard() {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [notifState, setNotifState] = useState(permissionState());
  const [installed, setInstalled] = useState(isStandalone());
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [blockedHelpOpen, setBlockedHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    // Re-sync permission when the user returns to the tab (they may have
    // toggled the browser setting in a separate window).
    const syncPerm = () => setNotifState(permissionState());
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('focus', syncPerm);
    document.addEventListener('visibilitychange', syncPerm);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('focus', syncPerm);
      document.removeEventListener('visibilitychange', syncPerm);
    };
  }, []);

  const ios = useMemo(() => isIOS(), []);
  const canInstall = !!deferredPrompt;
  const notifDone = notifState === 'granted';
  const installDone = installed || (!canInstall && !ios); // desktop already installed OR no install path
  const allDone = notifDone && installDone;

  if (allDone) return null;

  const enableNotifications = async () => {
    // Re-check the live permission BEFORE calling the browser API. Fixes
    // the case where the user just unblocked notifications in browser
    // settings but our React state was still cached as 'denied'.
    const live = permissionState();
    if (live !== notifState) setNotifState(live);
    if (live === 'denied') {
      setBlockedHelpOpen(true);
      return;
    }
    if (live === 'unsupported') {
      toast.error(isAr ? 'المتصفح لا يدعم الإشعارات' : 'Notifications not supported in this browser');
      return;
    }
    setBusy(true);
    try {
      const res = await ensurePushEnabled();
      if (res.granted) {
        toast.success(isAr ? 'تم تفعيل الإشعارات ✓' : 'Notifications enabled ✓');
        setNotifState('granted');
      } else if (res.reason === 'denied') {
        setNotifState('denied');
        setBlockedHelpOpen(true);
      } else if (res.reason === 'unsupported') {
        toast.error(isAr ? 'المتصفح لا يدعم الإشعارات' : 'Notifications not supported in this browser');
      } else {
        // 'default' after prompt = user dismissed the popup, or subscription failed
        toast.info(isAr
          ? 'لم يتم تفعيل الإشعارات — يرجى المحاولة مرة أخرى'
          : 'Notifications were not enabled — please try again');
      }
    } finally {
      setBusy(false);
    }
  };

  const installApp = async () => {
    if (ios && !canInstall) {
      setIosHelpOpen(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        toast.success(isAr ? 'تم تثبيت التطبيق ✓' : 'App installed ✓');
        setInstalled(true);
      }
    } catch { /* swallow */ }
    setDeferredPrompt(null);
  };

  return (
    <>
      <div
        dir={isAr ? 'rtl' : 'ltr'}
        className="mb-4 rounded-2xl border border-[#C4BFF0] bg-gradient-to-r from-[#EDE9FE] via-white to-[#F2F0FA] p-4 sm:p-5 shadow-sm"
        data-testid="pwa-home-card"
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#655dc6] text-white flex items-center justify-center">
            <Bell size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm sm:text-base font-semibold text-[#655dc6]">
                {isAr ? 'حسّن تجربتك في لوسنت' : 'Boost your Lucent experience'}
              </p>
              {notifDone && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                  <CheckCircle size={10} weight="fill" /> {isAr ? 'الإشعارات مفعّلة' : 'Notifs on'}
                </span>
              )}
              {installDone && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                  <CheckCircle size={10} weight="fill" /> {isAr ? 'مُثبَّت' : 'Installed'}
                </span>
              )}
            </div>
            <p className="text-[11px] sm:text-xs text-[#655dc6]/70 mt-1 leading-relaxed">
              {isAr
                ? 'فعّل الإشعارات لتصلك تنبيهات فورية عند اعتماد إجازة أو صرف راتب أو جاهزية خطاب — وثبّت التطبيق للوصول السريع من شاشة جوالك.'
                : 'Enable push notifications for instant alerts on leaves, payslips and letters — install the app for a one-tap experience.'}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {!notifDone && notifState !== 'denied' && (
                <Button
                  size="sm"
                  onClick={enableNotifications}
                  disabled={busy}
                  className="bg-[#655dc6] hover:bg-[#544cb0] text-white h-8 gap-2 disabled:opacity-70"
                  data-testid="home-enable-notif-btn"
                >
                  {busy
                    ? <span className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Bell size={14} />}
                  {isAr ? 'تفعيل الإشعارات' : 'Enable notifications'}
                </Button>
              )}
              {notifState === 'denied' && (
                <Button
                  size="sm"
                  onClick={() => setBlockedHelpOpen(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white h-8 gap-2"
                  data-testid="home-notif-blocked-btn"
                >
                  <LockKey size={14} weight="bold" />
                  {isAr ? 'الإشعارات محظورة — كيف أفعّلها؟' : 'Notifications blocked — how to enable?'}
                </Button>
              )}
              {!installDone && (canInstall || ios) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={installApp}
                  className="border-[#C4BFF0] text-[#655dc6] hover:bg-[#EDE9FE] h-8 gap-2"
                  data-testid="home-install-btn"
                >
                  <DownloadSimple size={14} />
                  {isAr ? 'تثبيت التطبيق' : 'Install app'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* iOS-specific install instructions */}
      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="sm:max-w-[440px]" data-testid="ios-install-help">
          <div dir={isAr ? 'rtl' : 'ltr'}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">
                {isAr ? 'تثبيت التطبيق على iPhone / iPad' : 'Install on iPhone / iPad'}
              </DialogTitle>
              <DialogDescription>
                {isAr
                  ? 'لتثبيت لوسنت كتطبيق على جهازك، اتبع الخطوات التالية في متصفح Safari:'
                  : 'Follow these steps in Safari to install Lucent as an app:'}
              </DialogDescription>
            </DialogHeader>
            <ol className="mt-4 space-y-3 text-sm text-[#2D2A5C]">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#655dc6] text-white flex items-center justify-center text-xs font-bold">1</span>
                <span className="flex-1">
                  {isAr ? 'اضغط زر المشاركة' : 'Tap the Share button'}
                  <ShareNetwork size={18} className="inline mx-2 text-[#655dc6]" weight="bold" />
                  {isAr ? 'أسفل الشاشة.' : 'at the bottom of the screen.'}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#655dc6] text-white flex items-center justify-center text-xs font-bold">2</span>
                <span className="flex-1">
                  {isAr ? 'اختر «إضافة إلى الشاشة الرئيسية»' : 'Choose "Add to Home Screen"'}
                  <Plus size={16} className="inline mx-2 text-[#655dc6]" weight="bold" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#655dc6] text-white flex items-center justify-center text-xs font-bold">3</span>
                <span className="flex-1">
                  {isAr
                    ? 'اضغط «إضافة» — ستظهر أيقونة لوسنت على شاشتك الرئيسية.'
                    : 'Tap "Add" — the Lucent icon will appear on your home screen.'}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">✓</span>
                <span className="flex-1 text-emerald-800">
                  {isAr
                    ? 'افتح التطبيق من الأيقونة الجديدة (وليس من Safari)، ثم فعّل الإشعارات.'
                    : 'Open the app from the new icon (not Safari), then enable notifications.'}
                </span>
              </li>
            </ol>
            <div className="mt-5 flex justify-end">
              <Button
                onClick={() => setIosHelpOpen(false)}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
                data-testid="ios-help-close"
              >
                {isAr ? 'حسناً' : 'Got it'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* "How to unblock notifications" dialog — appears when the user has
          already denied the permission and wants to re-enable it. */}
      <Dialog open={blockedHelpOpen} onOpenChange={setBlockedHelpOpen}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="sm:max-w-[480px]" data-testid="notif-unblock-help">
          <div dir={isAr ? 'rtl' : 'ltr'}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6] flex items-center gap-2">
                <LockKey size={20} weight="duotone" />
                {isAr ? 'كيف أفعّل الإشعارات؟' : 'How to enable notifications'}
              </DialogTitle>
              <DialogDescription>
                {isAr
                  ? 'الإشعارات محظورة في إعدادات المتصفح. اتبع الخطوات المناسبة لجهازك:'
                  : 'Notifications are blocked in browser settings. Follow the steps for your device:'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4 text-sm text-[#2D2A5C]">
              <div>
                <p className="font-semibold text-[#655dc6] mb-1">
                  {isAr ? 'Chrome / Edge على الحاسوب' : 'Chrome / Edge (Desktop)'}
                </p>
                <ol className="space-y-1 ps-4 list-decimal text-[13px] text-[#2D2A5C]/85">
                  <li>{isAr ? 'انقر على أيقونة القفل 🔒 بجانب عنوان الموقع.' : 'Click the lock icon 🔒 next to the URL.'}</li>
                  <li>{isAr ? 'ابحث عن «الإشعارات» وحدّد «السماح».' : 'Find "Notifications" and set it to "Allow".'}</li>
                  <li>{isAr ? 'أعد تحميل الصفحة ثم اضغط «تفعيل الإشعارات» مرة أخرى.' : 'Reload the page and click "Enable notifications" again.'}</li>
                </ol>
              </div>
              <div>
                <p className="font-semibold text-[#655dc6] mb-1">
                  {isAr ? 'Android — Chrome' : 'Android — Chrome'}
                </p>
                <ol className="space-y-1 ps-4 list-decimal text-[13px] text-[#2D2A5C]/85">
                  <li>{isAr ? 'اضغط على النقاط الثلاث ⋮ ← «إعدادات الموقع».' : 'Tap the three dots ⋮ → "Site settings".'}</li>
                  <li>{isAr ? 'اختر «الإشعارات» ← «السماح».' : 'Choose "Notifications" → "Allow".'}</li>
                  <li>{isAr ? 'ارجع للتطبيق وأعد المحاولة.' : 'Return to the app and try again.'}</li>
                </ol>
              </div>
              <div>
                <p className="font-semibold text-[#655dc6] mb-1">
                  {isAr ? 'iPhone / iPad (Safari)' : 'iPhone / iPad (Safari)'}
                </p>
                <p className="text-[13px] text-[#2D2A5C]/85">
                  {isAr
                    ? 'يجب أولاً تثبيت التطبيق على الشاشة الرئيسية (Share → Add to Home Screen)، ثم فتحه من الأيقونة الجديدة. عندها فقط ستظهر موافقة الإشعارات.'
                    : 'First install the app to your Home Screen (Share → Add to Home Screen), then open it from the new icon. Only then will the notification prompt appear.'}
                </p>
              </div>
              <div className="rounded-lg bg-[#F2F0FA] border border-[#C4BFF0] p-3 flex items-start gap-2">
                <ArrowSquareOut size={16} weight="bold" className="text-[#655dc6] shrink-0 mt-0.5" />
                <p className="text-[12px] text-[#655dc6]/90 leading-relaxed">
                  {isAr
                    ? 'بعد السماح من إعدادات المتصفح، عد إلى هذه الصفحة وسيتم تحديث الحالة تلقائياً.'
                    : 'After allowing in browser settings, return to this page — the status will refresh automatically.'}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setBlockedHelpOpen(false)}
                className="border-[#C4BFF0] text-[#655dc6] hover:bg-[#EDE9FE]"
                data-testid="unblock-help-close"
              >
                {isAr ? 'إغلاق' : 'Close'}
              </Button>
              <Button
                onClick={() => { setBlockedHelpOpen(false); setNotifState(permissionState()); enableNotifications(); }}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
                data-testid="unblock-help-retry"
              >
                <Bell size={14} className="me-1.5" />
                {isAr ? 'أعِد المحاولة' : 'Try again'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
